import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import debounce from 'lodash/debounce'
import { Combobox, ComboboxInput, ComboboxPopover, ComboboxList, ComboboxOption } from '@reach/combobox'
import '@reach/combobox/styles.css'
import vars from 'config/theme/vars'
import * as colors from 'config/theme/colors'
import { fontSize, fontSizeMobile, inputHeight, inputHeightMobile, fieldHorizontalPadding, fieldHorizontalPaddingMobile } from 'components/Fields/common/vars'
import FieldBase from './common/FieldBase'
import { useWebsiteConfig } from '../../hooks/configHooks'
import createAddressService from '../../services/addressService'
import Spinner from 'components/Spinner'



const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: 20;
  overflow: visible;
`

const SpinnerWrapper = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 21;
`

const StyledComboboxInput = styled(ComboboxInput)`
  display: block;
  font-family: inherit;
  color: ${colors.inputFontColor};
  font-size: ${fontSizeMobile}px;
  height: ${inputHeightMobile}px;
  line-height: ${inputHeightMobile}px;
  padding: 0 ${fieldHorizontalPaddingMobile}px;
  box-sizing: border-box;
  margin: 0;
  text-align: left;
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  border: 1px solid ${colors.inputBorderColor};
  border-radius: 0;
  background-color: ${colors.backgroundColor};
  caret-color: ${colors.inputFontColor};
  &::-ms-expand {
    display: none;
  }
  &::placeholder {
    color: ${colors.placeholderTextColor};
  }
  &:focus {
    outline: none;
    border: 2px solid ${colors.inputBorderColor};
  }
  @media (min-width: 768px) {
    font-size: ${fontSize}px;
    height: ${inputHeight}px;
    line-height: ${inputHeight}px;
    padding: 0 ${fieldHorizontalPadding}px;
  }
`

const StyledComboboxPopover = styled(ComboboxPopover)`
  margin-top: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: white;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1001;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
`

const StyledComboboxOption = styled(ComboboxOption)`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  &[data-selected] {
    background-color: #e6f3ff;
  }
`

const AddressLine = styled.span`
  font-size: 14px;
  color: #333;
`

const TownLine = styled.span`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`

const AddressLookup = ({
  onSelect,
  onAddressSelected,
  placeholder = 'Search for an address...',
  className,
  error,
  label,
  field = {},
  form = {}
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const { loqateBaseUrl, loqateFindEndpoint, loqateRetrieveEndpoint } = useWebsiteConfig()
  
  const addressService = React.useMemo(
    () => createAddressService(
      loqateBaseUrl,
      loqateFindEndpoint,
      loqateRetrieveEndpoint,
      process.env.loqateApiKey
    ),
    [loqateBaseUrl, loqateFindEndpoint, loqateRetrieveEndpoint]
  )

  const fetchSuggestions = useCallback(
    debounce(async (term) => {
      if (!term) {
        setSuggestions([])
        return
      }

      setIsLoading(true)
      try {
        const items = await addressService.findAddresses(term)
        setSuggestions(items)
      } catch (error) {
        console.error('Error fetching address suggestions:', error)
        setSuggestions([])
      } finally {
        setIsLoading(false)
      }
    }, 300),
    [addressService]
  )

  const handleInputChange = (event) => {
    const value = event.target.value
    setSearchTerm(value)
    fetchSuggestions(value)
  }

  const handleSelect = async (selectedValue) => {
    try {
      const selectedTerms = selectedValue.match(/^(.*?)\s*\(ID\|([^)]+)\)$/)
      const selectedText = selectedTerms ? selectedTerms[1] : selectedValue
      const selectedId = selectedTerms ? selectedTerms[2] : undefined
      const selectedItem = selectedId ? suggestions.find(item => item.Id === selectedId) : undefined
      
      if (!selectedItem) {
        throw new Error('Selected address not found')
      }

      if (selectedItem.Type !== 'Address') {
        const items = await addressService.findAddresses('', selectedId)
        if (items && items.length > 0) {
          const addressItems = items.filter(item => item.Type === 'Address')
          setSuggestions(addressItems)
          return
        }
      }

      if (selectedItem.Type === 'Address') {
        const address = await addressService.retrieveAddressDetails(selectedId)
        if (!address) {
          throw new Error('Address details not found')
        }

        setSearchTerm(selectedText)
        setSuggestions([])
        
        if (onSelect) {
          onSelect(address, form.setFieldValue)
        }
        if (onAddressSelected) {
          onAddressSelected(address)
        }
      }
    } catch (error) {
      console.error('Error fetching address details:', error)
    }
  }

  // Reach UI does not support scrolling the selected option into view when using keyboard navigation.
  // We manually scroll the selected option into view using this handler and a ref to the popover.
  const popoverRef = useRef(null)

  const onKeyDown = (event) => {
    if (!event.isDefaultPrevented()) {
      const container = popoverRef.current
      if (!container) return

      window.requestAnimationFrame(() => {
        const element = container.querySelector('[aria-selected="true"]')
        if (element) {
          const top = element.offsetTop - container.scrollTop
          const bottom = (container.scrollTop + container.clientHeight) - (element.offsetTop + element.clientHeight)

          if (bottom < 0) container.scrollTop -= bottom
          if (top < 0) container.scrollTop += top
        }
      })
    }
  }

  return (
    <FieldBase
      label={label}
      error={error}
      field={field}
      form={form}
      className={className}
      renderField={() => (
        <InputWrapper>
          <Combobox onSelect={handleSelect}>
            <StyledComboboxInput
              value={searchTerm}
              onChange={handleInputChange}
              placeholder={placeholder}
              aria-label="Address search"
              onKeyDown={onKeyDown}
            />
            {isLoading && (
              <SpinnerWrapper>
                <Spinner size={20} />
              </SpinnerWrapper>
            )}
            {suggestions.length > 0 && (
              <StyledComboboxPopover portal={true} ref={popoverRef}>
                <ComboboxList>
                  {suggestions.map((suggestion) => (
                    <StyledComboboxOption
                      key={suggestion.Id}
                      value={`${suggestion.Text}  (ID|${suggestion.Id})`}
                    >
                      <AddressLine>{suggestion.Text}</AddressLine>
                      {suggestion.Description && (
                        <TownLine>{suggestion.Description}</TownLine>
                      )}
                    </StyledComboboxOption>
                  ))}
                </ComboboxList>
              </StyledComboboxPopover>
            )}
          </Combobox>
        </InputWrapper>
      )}
    />
  )
}

AddressLookup.propTypes = {
  onSelect: PropTypes.func,
  onAddressSelected: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  error: PropTypes.string,
  label: PropTypes.string,
  field: PropTypes.object,
  form: PropTypes.object
}

export default AddressLookup
